import React, { useState, useEffect, useCallback } from "react";
import { ChartDataService } from "../../services/ChartDataService";
import { useUnifiedAuth } from "../../auth/useUnifiedAuth";
import { useSAPDataStore } from "../../store/SAPDataStore";
import { useChartCache } from "../../hooks/useChartCache";
import BaseChart from "./BaseChart";
import type { CategoryPointClick } from "./BaseChart";
import ShiftStatusModal from "./ShiftStatusModal";

// Backend returns { "2015-01-20 B1": { "Alarm 0 (undefined)": 1, ... }, ... }
type MachineStatusResults = Record<string, Record<string, number>>;

interface MachineStatusDataPoint {
  date: string;
  shift: string;
  status: string;
  value: number;
}

interface MachineStatusPerShiftChartProps {
  machineSerialNo?: string;
  lineId?: string;
  shifts?: string[];
  aggType?: "number" | "percent";
}

const MachineStatusPerShiftChart: React.FC<MachineStatusPerShiftChartProps> = ({
  machineSerialNo,
  shifts = [],
  aggType = "number",
}) => {
  const { token } = useUnifiedAuth();
  const { dateRange } = useSAPDataStore();
  const [rawChartData, setRawChartData] = useState<MachineStatusDataPoint[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Shift the interval drill-down is open for, or null when closed. */
  const [drillDown, setDrillDown] = useState<{
    shiftDate: string;
    shiftName: string;
  } | null>(null);

  // Left-click a column → that shift's operating-state timeline.
  // (Right-click still opens comments.)
  const handlePointClick = useCallback((point: CategoryPointClick) => {
    // Categories are built as `${date} ${shift}` (see chartData below), so the
    // shift name is the final token and the date is everything before it.
    const parts = point.category.trim().split(/\s+/);
    const shiftName = parts.pop() ?? "";
    const shiftDate = parts.join(" ");
    if (!shiftName || !/^\d{4}-\d{2}-\d{2}$/.test(shiftDate)) return;
    setDrillDown({ shiftDate, shiftName });
  }, []);

  // Use chart cache for better performance
  const chartCache = useChartCache<MachineStatusResults>({
    maxAge: 5 * 60 * 1000, // 5 minutes
    maxEntries: 30, // Cache up to 30 different chart configurations
  });

  const fetchMachineStatusData = async () => {
    // Don't fetch if no shifts selected
    if (!shifts || shifts.length === 0) {
      setLoading(false);
      return;
    }

    if (!machineSerialNo || !dateRange.start || !dateRange.end) return;
    if (!token) {
      setError("Authentication required");
      return;
    }

    // Check cache first (include aggType in cache key)
    const cachedData = chartCache.getCachedData(
      `pc.machine_status_per_shift_${aggType}`,
      machineSerialNo,
      dateRange,
      shifts,
    );

    if (cachedData) {
      processData(cachedData);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = ChartDataService.buildRequestParams(
        machineSerialNo,
        dateRange,
        shifts,
        undefined,
        { agg_type: aggType },
      );
      const response = await ChartDataService.fetchMachineStatusPerShift(
        params,
        token,
      );

      if (response.success && response.data) {
        const responseData = response.data as any;

        console.log("📊 MachineStatusPerShiftChart: Raw API response:", {
          success: response.success,
          responseKeys: Object.keys(responseData),
          data: responseData,
        });

        // Backend returns { status, results, error } - extract results
        const results = responseData.results || responseData;

        const resultKeys = results ? Object.keys(results) : [];
        console.log("📊 MachineStatusPerShiftChart: Extracted results:", {
          resultCount: resultKeys.length,
          firstKeys: resultKeys.slice(0, 5),
          sampleEntry: resultKeys[0]
            ? { key: resultKeys[0], value: results[resultKeys[0]] }
            : null,
        });

        chartCache.setCachedData(
          `pc.machine_status_per_shift_${aggType}`,
          machineSerialNo,
          dateRange,
          shifts,
          results,
        );
        processData(results);
      } else {
        setError("Failed to fetch machine status per shift data");
      }
    } catch (error) {
      console.error("Error fetching machine status per shift data:", error);
      setError("Error fetching machine status per shift data");
    } finally {
      setLoading(false);
    }
  };

  const processData = (data: MachineStatusResults) => {
    const processedData: MachineStatusDataPoint[] = [];

    // Data format: { "2015-01-20 B1": { "status": count, ... } }
    //          or: { "2015-01-20 B1": [{ name: "status", value: count }, ...] }
    Object.entries(data).forEach(([dateShiftKey, statusCounts]) => {
      // Parse combined "date shift" key - shift is the last part after the last space
      const keyParts = dateShiftKey.split(" ");
      const shift = keyParts.pop() || "";
      const date = keyParts.join(" ");

      // Only include if in selected shifts or no shifts filter
      if (shifts.length === 0 || shifts.includes(shift)) {
        if (Array.isArray(statusCounts)) {
          // Array format: [{name, value}, ...]
          statusCounts.forEach((item: any) => {
            processedData.push({
              date,
              shift,
              status: item.name || "Unknown",
              value: typeof item.value === "number" ? item.value : 0,
            });
          });
        } else if (statusCounts && typeof statusCounts === "object") {
          // Object format: { "status": count, ... }
          Object.entries(statusCounts).forEach(([status, value]) => {
            processedData.push({
              date,
              shift,
              status,
              value: typeof value === "number" ? value : 0,
            });
          });
        }
      }
    });

    // Sort by date
    processedData.sort((a, b) => a.date.localeCompare(b.date));
    setRawChartData(processedData);

    console.log("📊 MachineStatusPerShiftChart: Processed data:", {
      dataLength: processedData.length,
      sampleData: processedData.slice(0, 5),
      uniqueStatuses: [...new Set(processedData.map((d) => d.status))],
      uniqueShifts: [...new Set(processedData.map((d) => d.shift))],
      aggType,
      sampleValues: processedData.slice(0, 5).map((d) => ({
        status: d.status,
        value: d.value,
        formatted: aggType === "percent" ? `${d.value}%` : d.value.toString(),
      })),
    });
  };

  useEffect(() => {
    fetchMachineStatusData();
  }, [machineSerialNo, dateRange.start, dateRange.end, shifts, aggType]);

  // Transform data for BaseChart with stacked columns (similar to other stacked charts)
  const chartData = React.useMemo(() => {
    if (!rawChartData || rawChartData.length === 0)
      return { categories: [], series: [] };

    // Create time slots from the data (group by date+shift combination)
    const timeSlots: Set<string> = new Set();
    const statusTotals: Record<string, Record<string, number>> = {};

    rawChartData.forEach((item) => {
      const timeSlot = `${item.date.split("T")[0]} ${item.shift}`;
      timeSlots.add(timeSlot);

      if (!statusTotals[item.status]) {
        statusTotals[item.status] = {};
      }
      if (!statusTotals[item.status][timeSlot]) {
        statusTotals[item.status][timeSlot] = 0;
      }
      statusTotals[item.status][timeSlot] += item.value;
    });

    const sortedTimeSlots = Array.from(timeSlots).sort();

    // Sort statuses by total value (highest first) but separate INT/EXT
    const statusSums = Object.entries(statusTotals)
      .map(([status, timeData]) => ({
        status,
        total: Object.values(timeData).reduce((sum, value) => sum + value, 0),
        isInternal: status.includes("(Int)") || status.includes("(INT)"),
        isExternal: status.includes("(Ext)") || status.includes("(EXT)"),
      }))
      .sort((a, b) => {
        // First sort by type (Internal vs External), then by total value
        if (a.isInternal && b.isExternal) return -1;
        if (a.isExternal && b.isInternal) return 1;
        return b.total - a.total;
      });

    // Create series for each status (for stacking)
    const series = statusSums.map(({ status }) => ({
      name: status,
      data: sortedTimeSlots.map(
        (timeSlot) => statusTotals[status][timeSlot] || 0,
      ),
      // Color will be assigned automatically by BaseChart's color palette
    }));

    return {
      categories: sortedTimeSlots,
      series,
    };
  }, [rawChartData]);

  // Create custom options for stacked chart
  const customOptions = React.useMemo(
    () => ({
      plotOptions: {
        column: {
          stacking: (aggType === "percent" ? "percent" : "normal") as
            | "percent"
            | "normal",
          dataLabels: {
            enabled: false,
          },
          groupPadding: 0.2,
          pointPadding: 0.1,
          borderWidth: 0,
          borderRadius: 2,
        },
      },
      yAxis: {
        labels: {
          formatter: function (this: any) {
            const value = this.value;
            if (aggType === "percent") {
              return `${Math.round(value)}%`;
            } else {
              const hours = Math.floor(value / (1000 * 60 * 60));
              const minutes = Math.floor(
                (value % (1000 * 60 * 60)) / (1000 * 60),
              );
              const seconds = Math.floor((value % (1000 * 60)) / 1000);
              if (hours === 0 && minutes === 0) return `${seconds}s`;
              if (hours === 0) return `${minutes}m`;
              return `${hours}h`;
            }
          },
        },
      },
      tooltip: {
        shared: true,
        headerFormat: "<b>{point.x}</b><br/>",
        // Fallback only — `formatter` below takes precedence. Kept in sync with
        // it so a bypass can't print raw milliseconds.
        pointFormat:
          aggType === "percent"
            ? '<span style="color:{series.color}">●</span> {series.name}: <b>{point.percentage:.1f}%</b><br/>'
            : '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y} ms</b><br/>',
        formatter: function (this: any) {
          const category =
            (this.points?.[0]?.point as any)?.category ||
            (this.points?.[0]?.series.xAxis?.categories &&
              this.points[0].series.xAxis.categories[this.x as number]) ||
            String(this.x ?? "");
          let tooltipContent = `<b>${category}</b><br/>`;

          // `point.y` is always the raw duration in ms — `stacking: 'percent'`
          // only changes how Highcharts DRAWS the column, so the percentage has
          // to come from `point.percentage`. Formatting y as a percentage is what
          // produced values like "27378000%".
          const formatDuration = (ms: number): string => {
            const hours = Math.floor(ms / (1000 * 60 * 60));
            const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((ms % (1000 * 60)) / 1000);
            if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
            if (minutes > 0) return `${minutes}m ${seconds}s`;
            return `${seconds}s`;
          };

          /** Percent mode shows the share plus the duration behind it. */
          const formatPoint = (point: any): string => {
            const duration = formatDuration(point.y);
            if (aggType !== "percent") return duration;
            const share =
              typeof point.percentage === "number" ? point.percentage : 0;
            return `${share.toFixed(1)}% (${duration})`;
          };

          const formatValue = formatDuration;

          if (aggType === "percent") {
            tooltipContent += "<strong>Status Distribution:</strong><br/>";
          } else {
            let totalValue = 0;
            this.points.forEach((point: any) => {
              totalValue += point.y;
            });
            tooltipContent += `<strong>Total: ${formatValue(totalValue)}</strong><br/>`;
          }
          tooltipContent +=
            '<hr style="margin: 4px 0; border-color: #e5e7eb;">';

          this.points.forEach((point: any) => {
            if (point.y > 0) {
              tooltipContent += `<span style="color:${point.color}">●</span> ${point.series.name}: <strong>${formatPoint(point)}</strong><br/>`;
            }
          });

          return tooltipContent;
        },
      },
    }),
    [aggType],
  );

  // Show loading animation first
  if (loading) {
    return (
      <div className="w-full">
        <BaseChart
          type="column"
          height={500}
          categories={[]}
          series={[]}
          loading={true}
        />
      </div>
    );
  }

  if (!rawChartData || rawChartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h3 className="text-lg font-semibold theme-text-primary mb-2">
            No Machine Status Data
          </h3>
          <p className="theme-text-secondary">
            No machine status data found for the selected period.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <BaseChart
        commentProps={{
          chartType: "machine_status_per_shift",
          machineBK: machineSerialNo,
        }}
        onPointClick={handlePointClick}
        type="column"
        height={500}
        categories={chartData.categories}
        series={chartData.series}
        yAxis={{
          min: 0,
          max: aggType === "percent" ? 100 : undefined,
          title: aggType === "percent" ? "Percentage" : "Count",
        }}
        customOptions={customOptions}
        loading={loading}
        error={error}
        onRetry={fetchMachineStatusData}
        legend={{
          enabled: true,
          position: "bottom",
        }}
        noDataMessage={
          !machineSerialNo
            ? "No machine selected"
            : !dateRange.start || !dateRange.end
              ? "Please select a date range"
              : !shifts || shifts.length === 0
                ? "No shifts selected or available for the selected timeframe"
                : "No machine status data found for the selected period"
        }
      />

      {drillDown && machineSerialNo && (
        <ShiftStatusModal
          machineSerialNo={machineSerialNo}
          shiftDate={drillDown.shiftDate}
          shiftName={drillDown.shiftName}
          onClose={() => setDrillDown(null)}
        />
      )}
    </div>
  );
};

export default MachineStatusPerShiftChart;
